// Build your combo (sections/combo-builder.liquid). Tracks the plans a shopper adds and shows the tier they
// reach: the tier strip, progress bar, hint, list and totals. The discount itself is Shopify's (automatic
// discounts with the same tiers); the numbers here are a preview of it. Also filters the list by search.
if (!customElements.get('combo-builder')) {
  customElements.define(
    'combo-builder',
    class ComboBuilder extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;
        this.ready = true;

        // "2:5,3:10,4:15" -> [{ count: 2, percent: 5 }, ...], fewest plans first
        this.tiers = (this.dataset.tiers || '')
          .split(',')
          .map((pair) => pair.split(':').map(Number))
          .filter(([count, percent]) => count > 0 && percent > 0)
          .map(([count, percent]) => ({ count, percent }))
          .sort((a, b) => a.count - b.count);
        this.moneyFormat = this.dataset.moneyFormat || 'Tk {{amount}}';

        this.addEventListener('change', (event) => {
          if (event.target.matches('[data-combo-search]')) return;
          this.showError('');
          // A row shows the price of the plan picked in its dropdown
          if (event.target.matches('[data-combo-plan]')) {
            const option = event.target.selectedOptions[0];
            const price = event.target.closest('[data-combo-card]')?.querySelector('[data-combo-row-price]');
            if (option && price) price.textContent = this.formatMoney(Number(option.dataset.price) || 0);
          }
          this.update();
          this.trackChange(event.target);
        });
        this.querySelector('[data-combo-search]')?.addEventListener('input', (event) => this.filter(event.target.value));
        this.querySelector('[data-combo-submit]')?.addEventListener('click', () => this.addToCart());
        // Which questions people open (toggle doesn't bubble, so listen while capturing)
        this.addEventListener(
          'toggle',
          (event) => {
            if (event.target.matches?.('.combo-faq__item') && event.target.open) {
              this.track('combo_faq_opened', { question: event.target.querySelector('summary')?.textContent.trim() });
            }
          },
          true
        );
        this.setUpWindow();
        // Back from the cart restores this page from the back/forward cache with the button still busy
        window.addEventListener('pageshow', () => this.setBusy(false));
        this.update();
        this.track('combo_viewed', { services: this.querySelectorAll('[data-combo-card]').length });
      }

      // Analytics: every event goes to Shopify's customer events (Settings > Customer events, where a custom
      // pixel forwards them to Google Analytics) and to Microsoft Clarity when its app is installed, so
      // recordings can be filtered by what people did. Does nothing when neither is listening.
      track(name, data = {}) {
        try {
          window.Shopify?.analytics?.publish?.(name, data);
        } catch (error) {}
        try {
          if (typeof window.clarity === 'function') {
            window.clarity('event', name);
            window.clarity('set', 'combo_last_action', name);
          }
        } catch (error) {}
      }

      // Plan added, removed or switched; the combo's size comes from the update that just ran
      trackChange(target) {
        const row = target.closest('[data-combo-card]');
        if (!row) return;
        const option = row.querySelector('[data-combo-plan]')?.selectedOptions[0];
        const details = {
          service: row.dataset.title,
          plan: option?.dataset.plan,
          price: (Number(option?.dataset.price) || 0) / 100,
          plans_in_combo: this.lastCount,
        };
        if (target.matches('[data-combo-add]')) {
          this.track(target.checked ? 'combo_plan_added' : 'combo_plan_removed', details);
        } else if (target.matches('[data-combo-plan]')) {
          this.track('combo_plan_changed', { ...details, in_combo: Boolean(row.querySelector('[data-combo-add]')?.checked) });
        }
      }

      // Adds every plan in the combo to the cart in one request, then opens the cart, where Shopify applies
      // the combo discount. On failure the button comes back and Shopify's message is shown.
      async addToCart() {
        const button = this.querySelector('[data-combo-submit]');
        if (!button || button.disabled || this.busy) return;

        const items = [...this.querySelectorAll('[data-combo-card]')]
          .filter((row) => row.querySelector('[data-combo-add]')?.checked)
          .map((row) => row.querySelector('[data-combo-plan]'))
          .filter((select) => select && select.value && !select.selectedOptions[0]?.disabled)
          .map((select) => ({ id: Number(select.value), quantity: 1 }));
        if (items.length < (this.tiers[0]?.count || 1)) return;

        const routes = window.routes || {};
        this.showError('');
        this.setBusy(true);
        try {
          const response = await fetch(`${routes.cart_add_url || '/cart/add'}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items }),
          });
          const data = await response.json().catch(() => ({}));
          if (!response.ok) throw new Error(data.description || data.message || 'Could not add the combo to your cart.');
          const plans = this.selectedPlans();
          this.track('combo_added_to_cart', {
            plans: plans.length,
            services: plans.map((plan) => plan.title).join(', '),
            subtotal: this.lastSubtotal / 100,
            discount_percent: this.lastPercent,
            total: this.lastTotal / 100,
          });
          // A moment for the event to leave before the page changes
          setTimeout(() => {
            window.location.href = routes.cart_url || '/cart';
          }, 150);
        } catch (error) {
          this.setBusy(false);
          this.showError(error.message || 'Could not add the combo to your cart. Please try again.');
          this.track('combo_add_to_cart_failed', { message: error.message || 'unknown' });
        }
      }

      setBusy(busy) {
        this.busy = busy;
        const button = this.querySelector('[data-combo-submit]');
        const label = this.querySelector('[data-combo-button-label]');
        if (!button || !label) return;
        if (!this.idleLabel) this.idleLabel = label.textContent;
        label.textContent = busy ? button.dataset.addingLabel || this.idleLabel : this.idleLabel;
        button.classList.toggle('is-busy', busy);
        if (busy) {
          button.setAttribute('aria-disabled', 'true');
        } else {
          button.removeAttribute('aria-disabled');
        }
      }

      showError(message) {
        const error = this.querySelector('[data-combo-error]');
        if (!error) return;
        error.textContent = message;
        error.hidden = !message;
      }

      // The 2 × 2 window scrolls sideways. The arrows move it one view (two columns) at a time, are disabled
      // at either end and hidden when every service fits. Scroll position is read once per animation frame.
      setUpWindow() {
        this.scroller = this.querySelector('[data-combo-scroller]');
        const prev = this.querySelector('[data-combo-prev]');
        const next = this.querySelector('[data-combo-next]');
        if (!this.scroller || !prev || !next) return;

        const page = (direction) => {
          const gap = parseFloat(getComputedStyle(this.scroller).columnGap) || 0;
          const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          this.scroller.scrollBy({ left: direction * (this.scroller.clientWidth + gap), behavior: smooth ? 'smooth' : 'auto' });
        };
        prev.addEventListener('click', () => {
          page(-1);
          this.track('combo_browsed', { direction: 'previous' });
        });
        next.addEventListener('click', () => {
          page(1);
          this.track('combo_browsed', { direction: 'next' });
        });

        let ticking = false;
        this.updateWindow = () => {
          ticking = false;
          const { scrollLeft, scrollWidth, clientWidth } = this.scroller;
          const overflow = scrollWidth > clientWidth + 2;
          prev.parentElement.hidden = !overflow;
          prev.disabled = scrollLeft <= 2;
          next.disabled = scrollLeft + clientWidth >= scrollWidth - 2;
        };
        const requestUpdate = () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(this.updateWindow);
        };
        this.scroller.addEventListener('scroll', requestUpdate, { passive: true });
        window.addEventListener('resize', requestUpdate, { passive: true });
        this.updateWindow();
      }

      // "Prime video" finds "PrimeVideo Premium": compare letters and digits only
      static normalize(text) {
        return text.toLowerCase().replace(/[^a-z0-9]/g, '');
      }

      filter(query) {
        const needle = ComboBuilder.normalize(query);
        let shown = 0;
        this.querySelectorAll('[data-combo-card]').forEach((row) => {
          const match = !needle || ComboBuilder.normalize(row.dataset.title).includes(needle);
          row.hidden = !match;
          if (match) shown += 1;
        });
        const noResults = this.querySelector('[data-combo-no-results]');
        if (noResults) noResults.hidden = shown > 0;
        this.setText('[data-combo-query]', query.trim());
        // Results start at the beginning of the window
        if (this.scroller) {
          this.scroller.scrollLeft = 0;
          this.updateWindow?.();
        }
        // Report a search once typing pauses, not on every keystroke
        clearTimeout(this.searchTimer);
        const typed = query.trim();
        if (typed) {
          this.searchTimer = setTimeout(() => this.track('combo_searched', { query: typed, results: shown }), 1000);
        }
      }

      formatMoney(cents) {
        const value = cents / 100;
        const amount = value.toLocaleString('en-US', {
          minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
          maximumFractionDigits: 2,
        });
        return this.moneyFormat.replace(/\{\{\s*\w+\s*\}\}/, amount);
      }

      selectedPlans() {
        return [...this.querySelectorAll('[data-combo-card]')]
          .filter((row) => row.querySelector('[data-combo-add]')?.checked)
          .map((row) => {
            const option = row.querySelector('[data-combo-plan]')?.selectedOptions[0];
            return option && !option.disabled
              ? {
                  title: row.dataset.title,
                  image: row.dataset.image,
                  plan: option.dataset.plan,
                  price: Number(option.dataset.price) || 0,
                }
              : null;
          })
          .filter(Boolean);
      }

      update() {
        const tiers = this.tiers;
        if (!tiers.length) return;
        const plans = this.selectedPlans();
        const count = plans.length;

        let reached = -1;
        tiers.forEach((tier, index) => {
          if (count >= tier.count) reached = index;
        });
        const percent = reached >= 0 ? tiers[reached].percent : 0;
        const subtotal = plans.reduce((sum, plan) => sum + plan.price, 0);
        const saving = Math.round((subtotal * percent) / 100);
        const total = subtotal - saving;

        // Remembered for analytics; a higher tier than before is worth an event of its own
        if (this.lastReached !== undefined && reached > this.lastReached) {
          this.track('combo_tier_reached', { discount_percent: percent, plans: count });
        }
        this.lastReached = reached;
        this.lastCount = count;
        this.lastPercent = percent;
        this.lastTotal = total;

        this.renderSlots(plans);
        this.setText('[data-combo-count]', count);
        this.renderHint(count, reached, percent);
        this.renderList(plans);

        const totals = this.querySelector('[data-combo-totals]');
        if (totals) totals.hidden = count === 0;
        const summary = this.querySelector('.combo-summary');
        const changed = this.lastSubtotal !== undefined && this.lastSubtotal !== subtotal;
        this.lastSubtotal = subtotal;
        this.setText('[data-combo-subtotal]', this.formatMoney(subtotal));
        this.setText('[data-combo-saving]', this.formatMoney(saving));
        this.setText('[data-combo-percent]', percent);
        this.setText('[data-combo-total]', this.formatMoney(total));
        const savingRow = this.querySelector('[data-combo-saving-row]');
        if (savingRow) savingRow.hidden = percent === 0;
        if (changed && summary) {
          // Blurred state, style flush, release: the new numbers settle in (same as the product price)
          summary.classList.add('is-updating');
          void summary.offsetWidth;
          summary.classList.remove('is-updating');
        }

        const button = this.querySelector('[data-combo-submit]');
        if (button) button.disabled = count < tiers[0].count;
        this.setText('[data-combo-button-total]', count ? this.formatMoney(total) : '');
      }

      renderHint(count, reached, percent) {
        const hint = this.querySelector('[data-combo-hint]');
        if (!hint) return;
        const next = this.tiers[reached + 1];
        const plural = (n) => (n === 1 ? 'plan' : 'plans');

        // Numbers only come from the tier settings and the count, so building HTML here is safe
        if (reached < 0) {
          const needed = next.count - count;
          hint.innerHTML =
            count === 0
              ? `Pick ${next.count} plans to get <strong>${next.percent}% off</strong>`
              : `Add ${needed} more ${plural(needed)} to get <strong>${next.percent}% off</strong>`;
        } else if (next) {
          const needed = next.count - count;
          hint.innerHTML = `<strong>${percent}% off</strong> unlocked. Add ${needed} more for ${next.percent}% off`;
        } else {
          hint.innerHTML = `<strong>${percent}% off</strong>, the biggest combo discount`;
        }
      }

      // Slot n holds the nth plan's logo; past the last slot it shows "+n". A slot is "reached" once the combo
      // has that many plans, which lights its tier label.
      renderSlots(plans) {
        const slots = [...this.querySelectorAll('[data-combo-slot]')];
        slots.forEach((slot, index) => {
          const box = slot.querySelector('.combo-slot__box');
          const overflow = index === slots.length - 1 && plans.length > slots.length;
          const plan = plans[index];
          const key = overflow ? `+${plans.length - index}` : plan ? plan.image : '';
          slot.classList.toggle('is-filled', Boolean(plan));
          slot.classList.toggle('is-reached', plans.length > index);
          if (!box || box.dataset.key === key) return;
          box.dataset.key = key;

          if (overflow) {
            box.replaceChildren(key);
          } else if (plan && plan.image) {
            const image = document.createElement('img');
            image.src = plan.image;
            image.alt = '';
            image.width = 64;
            image.height = 64;
            box.replaceChildren(image);
          } else {
            box.replaceChildren();
          }
        });
      }

      renderList(plans) {
        const list = this.querySelector('[data-combo-list]');
        if (!list) return;

        // Rows stay in the DOM untouched; only a new plan is inserted (and plays the enter transition) and
        // dropped plans are removed. Moving a row would replay its transition.
        const keys = plans.map((plan) => `${plan.title}|${plan.plan}`);
        [...list.children].forEach((item) => {
          if (!keys.includes(item.dataset.key)) item.remove();
        });
        plans.forEach((plan, index) => {
          const current = list.children[index];
          if (current && current.dataset.key === keys[index]) return;
          list.insertBefore(this.createRow(plan, keys[index]), current || null);
        });
      }

      createRow(plan, key) {
        const item = document.createElement('li');
        item.className = 'combo-summary__item';
        item.dataset.key = key;

        const text = document.createElement('span');
        text.className = 'combo-summary__item-text';
        const name = document.createElement('span');
        name.className = 'combo-summary__name';
        name.textContent = plan.title;
        const label = document.createElement('span');
        label.className = 'combo-summary__plan';
        label.textContent = plan.plan;
        text.append(name, label);

        const price = document.createElement('span');
        price.className = 'combo-summary__price';
        price.textContent = this.formatMoney(plan.price);

        item.append(text, price);
        return item;
      }

      setText(selector, value) {
        const element = this.querySelector(selector);
        if (element) element.textContent = value;
      }
    }
  );
}
